import React, { type FC } from "react";

type NestedWrapperProps<T> = T & {
  /** The child component which is necessary at all levels */
  renderChild: FC<
    Omit<NestedWrapperProps<T>, "renderChild" | "renderParent" | "nested">
  >;
  /** The parent wrapper which is necessary at a top level */
  renderParent: FC<
    Omit<NestedWrapperProps<T>, "renderChild" | "renderParent" | "nested">
  >;
  /** Whether the parent component is nested or not */
  nested?: boolean;
};

/**
 * A component which either returns just the child component — if the parent component is nested — or it returns the parent component with the child component as a child if it is not nested.
 *
 * @type {React.FC<InferProps<NestedWrapperProps<T>>}
 * @returns {React.ReactElement}
 */
export const NestedWrapper = <T,>({
  renderChild: Child,
  renderParent: Parent,
  nested,
  ...rest
}: NestedWrapperProps<T>) => {
  if (nested) {
    return <Child {...rest} />;
  }

  return (
    <Parent {...rest}>
      <Child {...rest} />
    </Parent>
  );
};
